/* FH discovery v2: native theme surfaces; FH Search provider with Shopify fallback.
 * No portal request is needed to render or browse collection navigation.
 */
(() => {
  'use strict';
  if (customElements.get('fh-discovery')) return;
  const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

  function words(value) {
    return normalize(value).replace(/\bfoamcore\b/g, 'foam core').split(' ').filter(Boolean).map(token => ({ heads: 'head', cutouts: 'cutout' }[token] || token));
  }

  /**
   * Native predictive search can relax a multiword query or expand Bluey to Blues.
   * Prefer candidates that retain every identity token, allowing the final token
   * to be a prefix while typing. If no identity matches, retain native typo recovery.
   * Custom is an explicit shopper intent: never refill it with non-custom products.
   * This guard does not affect authoritative index results or boost custom globally.
   */
  function retainPredictiveIntent(query, items, products = true) {
    const tokens = words(query).filter(token => !['fathead', 'fatheads'].includes(token));
    if (!tokens.length) return items;
    const identity = item => words(`${item.title ?? ''} ${item.handle ?? ''}`);
    const personalized = token => ['custom', 'personalized', 'personalised'].includes(token);
    const custom = tokens.some(personalized);
    let candidates = items;
    if (products && custom) {
      candidates = candidates.filter(item => identity(item).some(personalized));
      // Material intent is explicit for these Fathead cutout materials. Do not
      // infer other materials from unavailable native metadata.
      const material = [
        ...(tokens.includes('cardstock') ? ['cardstock'] : []),
        ...(tokens.includes('foam') && tokens.includes('core') ? ['foam', 'core'] : []),
      ];
      if (material.length) candidates = candidates.filter(item => material.every(token => identity(item).includes(token)));
    }
    const exact = candidates.filter(item => {
      const target = identity(item);
      return tokens.every((token, i) =>
        (custom && personalized(token) && target.some(personalized)) ||
        target.some(word => i === tokens.length - 1 ? word.startsWith(token) : word === token),
      );
    });
    return exact.length ? exact : candidates;
  }

  const el = (tag, className, text) => { const node = document.createElement(tag); if (className) node.className = className; if (text != null) node.textContent = text; return node; };
  const arrowIcon = () => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.7');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M4 12h16m-6-6 6 6-6 6');
    svg.append(path);
    return svg;
  };
  const internalURL = value => { try { const url = new URL(value, location.origin); return url.origin === location.origin || ['fathead.com', 'www.fathead.com', 'fathead-llc.myshopify.com'].includes(url.hostname) ? url.pathname + url.search + url.hash : null; } catch { return null; } };
  const imageURL = value => { if (!value) return null; try { const url = new URL(typeof value === 'object' ? value?.url : value, location.origin); return url.protocol === 'https:' && (url.hostname === 'cdn.shopify.com' || url.hostname === 'fathead.com' || url.hostname.endsWith('.myshopify.com')) ? url.href : null; } catch { return null; } };

  class FHDiscovery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.events = new AbortController();
      this.explore = this.querySelector('[data-explore]');
      this.header = this.querySelector('.fh-header');
      this.headerSpace = document.createElement('div');
      this.headerSpace.setAttribute('aria-hidden', 'true');
      this.search = this.querySelector('[data-search]');
      this.query = this.querySelector('[data-query]');
      this.results = this.querySelector('[data-search-results]');
      this.discover = this.querySelector('[data-search-discover]');
      this.status = this.querySelector('[data-search-status]');
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.activePanel = 'featured';
      this.revision = 0;
      this.scrollPositions = new Map();
      this.phrases = (this.dataset.phrases || '').split('|').map(x => x.trim()).filter(Boolean).slice(0, 8);
      this.index = [...new Map([...this.querySelectorAll('[data-discovery-link]')].map(a => [a.getAttribute('href'), { url: a.getAttribute('href'), title: a.textContent.trim().replace(/^Shop all /, ''), group: a.dataset.group || '', image: a.querySelector('img')?.getAttribute('src') }])).values()];
      this.history = this.readHistory();
      this.listen(this, 'click', event => this.onClick(event));
      this.listen(this.query, 'input', () => this.onQuery());
      this.listen(this, 'input', event => { if (event.target.matches('[data-team-filter]')) this.filterDirectory(event.target); });
      this.listen(this.querySelector('[data-search-form]'), 'submit', () => { this.querySelector('[data-query-value]').value = this.query.value.trim(); this.saveHistory(this.query.value); });
      this.listen(document, 'fh:surface:opening', event => this.releaseSurface(event.detail));
      this.listen(document, 'click', event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
        if (event.target.closest('.customer-account__parent-link')) { this.closeAll(false); if (window.theme?.cartDrawer?.isOpen()) window.theme.cartDrawer.close(); }
        if (event.target.closest('.header-cart > a')) { this.closeAll(false); window.FatheadAccount?.close(); }
      }, { capture: true });
      this.listen(document, 'caffeine:cart:open', () => { this.closeAll(false); window.FatheadAccount?.close(); });
      this.listen(document, 'keydown', event => {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); this.openSearch(); }
      });
      [this.explore, this.search].forEach(dialog => {
        this.listen(dialog, 'cancel', event => { event.preventDefault(); const filter = dialog === this.explore ? dialog.querySelector('[data-panel="' + this.activePanel + '"] [data-team-filter]') : null; if (filter?.value) { filter.value = ''; this.filterDirectory(filter); filter.focus(); } else this.dismiss(dialog); });
        this.listen(dialog, 'click', event => {
          if (event.target !== dialog) return;
          const box = dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dismiss(dialog);
        });
        this.listen(dialog, 'close', () => {
          if (dialog === this.explore && !dialog.open) this.restoreHeader();
          if (dialog === this.explore && !dialog.open) this.querySelectorAll('[data-open-explore],[data-open-customs]').forEach(b => b.setAttribute('aria-expanded', 'false'));
          this.releaseScrollLock();
        });
        this.listen(dialog, 'keydown', event => {
          if (event.key === 'Escape') {
            event.preventDefault(); event.stopPropagation();
            const filter = dialog === this.explore ? dialog.querySelector('[data-panel="' + this.activePanel + '"] [data-team-filter]') : null;
            if (filter?.value) { filter.value = ''; this.filterDirectory(filter); filter.focus(); }
            else this.dismiss(dialog);
            return;
          }
          if (event.key !== 'Tab') return;
          const nodes = [...dialog.querySelectorAll('a[href],button:not([disabled]),input:not([type="hidden"]),[tabindex="0"]')].filter(n => n.getClientRects().length && !n.closest('[hidden]'));
          const first = nodes[0], last = nodes[nodes.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        });
      });
      this.listen(window, 'resize', () => this.updatePosition());
      this.listen(window, 'scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = 0; this.syncHeader(); });
      }, { passive: true });
      this.headerGroup = this.closest('.header-group');
      this.headerObserver = new ResizeObserver(() => this.updatePosition());
      this.headerObserver.observe(this.header);
      if (this.headerGroup) this.headerObserver.observe(this.headerGroup);
      this.syncHeader();
      this.updatePosition();
      this.listen(document, 'shopify:block:select', event => {
        const block = this.querySelector(`[data-shopify-editor-block*='"id":"${CSS.escape(event.detail.blockId)}"']`);
        if (block && this.openExplore() !== false) { this.showPanel(block.closest('[data-panel]')?.dataset.panel || 'featured'); }
      });
      this.renderIdeas(); this.renderHistory(); this.startPlaceholder(); this.initCustomsVideo();
      this.setAttribute('data-ready', 'true');
      // Stable public surface API for the account/search apps and future portal editor previews.
      window.FatheadDiscovery = { open: panel => { if (this.openExplore() === false) return false; if (panel) this.showPanel(panel); }, close: () => this.closeAll(), search: query => this.openSearch(query), version: '2.0-preview' };
    }
    disconnectedCallback() { this.cancelDismiss(this.explore); this.cancelDismiss(this.search); cancelAnimationFrame(this.scrollFrame); this.scrollFrame = 0; this.headerObserver?.disconnect(); this.customsObserver?.disconnect(); this.customsVideo?.pause(); this.events?.abort(); this.request?.abort(); clearTimeout(this.debounce); clearTimeout(this.phraseTimer); this.restoreHeader(); if (this.explore?.open || this.search?.open) document.body.classList.remove('fh-overlay-open'); this.ready = false; }
    listen(target, type, fn, options = {}) { target?.addEventListener(type, fn, { ...options, signal: this.events.signal }); }
    syncHeader() {
      // Keep body scroll locking from changing the header while a surface is open.
      if (this.explore.open || this.search.open) return;
      const y = Math.max(0, window.scrollY);
      // The separate thresholds exceed the height change, preventing scroll-anchor flicker.
      if (y > 80) this.setAttribute('data-header-compact', '');
      else if (y < 24) this.removeAttribute('data-header-compact');
    }
    updatePosition() {
      const desktop = matchMedia('(min-width: 900px)').matches;
      if (this.headerGroup) {
        const section = this.closest('.fh-native-header-section');
        this.headerGroup.style.setProperty('--fh-sticky-offset', `${section?.offsetTop || 0}px`);
      }
      if (!desktop) this.restoreHeader();
      const box = (this.headerSpace.isConnected ? this.headerSpace : this.header).getBoundingClientRect();
      const height = this.header.getBoundingClientRect().height;
      this.style.setProperty('--fh-header-top', `${Math.max(0, box.top)}px`);
      this.style.setProperty('--fh-header-height', `${height}px`);
      document.documentElement.style.setProperty('--fh-sticky-header-height', `${height}px`);
      this.style.setProperty('--fh-header-bottom', `${Math.max(0, box.top + height)}px`);
      if (this.headerSpace.isConnected) this.headerSpace.style.height = `${height}px`;
      if (desktop && this.explore.open) this.joinHeader();
    }
    joinHeader() {
      if (!matchMedia('(min-width: 900px)').matches || this.headerSpace.isConnected) return;
      // Keep the real header inside the modal's focus scope; never clone app/cart controls.
      this.headerSpace.style.height = `${this.header.getBoundingClientRect().height}px`;
      this.header.before(this.headerSpace);
      this.explore.prepend(this.header);
    }
    restoreHeader() {
      if (!this.headerSpace?.parentNode) return;
      this.headerSpace.replaceWith(this.header);
    }
    scroller() { return matchMedia('(max-width: 899px)').matches ? this.querySelector('.fh-explore__layout') : this.querySelector('[data-nav-scroll]'); }
    releaseSurface(detail) {
      if (!detail || detail.owner === this) return;
      const dialog = this.search.open ? this.search : this.explore.open ? this.explore : null;
      if (!dialog) return;
      if (dialog.contains(document.activeElement)) detail.returnFocus ||= dialog === this.search ? this.searchFocus : this.lastFocus;
      this.closeAll(false);
    }
    releaseScrollLock() {
      // Native close events are queued; another surface may already own the lock.
      if (!this.explore.open && !this.search.open && !document.querySelector('fh-catalog [data-facet-dialog][open]')) document.body.classList.remove('fh-overlay-open');
    }
    closeOthers() {
      const detail = { surface: 'discovery', owner: this, returnFocus: null };
      document.dispatchEvent(new CustomEvent('fh:surface:opening', { detail }));
      if (window.theme?.cartDrawer?.isOpen()) window.theme.cartDrawer.close();
      window.FatheadAccount?.close();
      return detail.returnFocus;
    }
    customizerOwnsSurface() {
      // The hosted editor may still use a fixed div or the new native dialog.
      // Never hide a draft or request its dismissal merely to open discovery.
      const overlay = document.querySelector('[data-fathead-overlay]');
      return Boolean(overlay?.getClientRects?.().length);
    }
    openExplore() {
      if (this.customizerOwnsSurface()) return false;
      this.cancelDismiss(this.explore);
      const transferredFocus = this.closeOthers(); this.searchReturn = false;
      if (this.search.open) this.closeDialog(this.search, false);
      const source = transferredFocus || document.activeElement;
      if (source !== document.body && source !== document.documentElement && !this.search.contains(source)) this.lastFocus = source;
      this.lastFocus ||= this.querySelector(matchMedia('(min-width: 900px)').matches ? '.fh-explore-trigger' : '.fh-mobile-menu');
      this.updatePosition();
      this.joinHeader();
      if (!this.explore.open) this.explore.showModal();
      document.body.classList.add('fh-overlay-open');
      this.querySelectorAll('[data-open-explore],[data-open-customs]').forEach(b => b.setAttribute('aria-expanded', 'true'));
      this.showPanel(this.activePanel || 'featured', false);
      this.explore.querySelector('[data-close]').focus({ preventScroll: true });
    }
    openSearch(value) {
      if (this.customizerOwnsSurface()) return false;
      this.cancelDismiss(this.search);
      const transferredFocus = this.closeOthers();
      if (!this.search.open) {
        this.searchReturn = this.explore.open;
        this.searchFocus = transferredFocus || document.activeElement;
        if (this.explore.open) this.closeDialog(this.explore, false);
        this.search.showModal();
        window.FHDiscoveryAnalytics?.open();
        document.body.classList.add('fh-overlay-open');
      }
      if (typeof value === 'string') { this.query.value = value; this.onQuery(); } else if (this.query.value) this.onQuery();
      this.query.focus({ preventScroll: true });
      if (!this.query.value) this.renderHistory();
      this.syncCustomsVideo();
    }
    closeDialog(dialog, restore = true) {
      this.cancelDismiss(dialog);
      if (!dialog.open) return;
      if (dialog === this.explore) this.restoreHeader();
      dialog.close();
      if (dialog === this.explore) this.querySelectorAll('[data-open-explore],[data-open-customs]').forEach(b => b.setAttribute('aria-expanded', 'false'));
      if (dialog === this.search) { this.syncCustomsVideo(); window.FHDiscoveryAnalytics?.close(); clearTimeout(this.analyticsTimer); this.revision++; this.request?.abort(); clearTimeout(this.debounce); }
      this.releaseScrollLock();
      if (restore) {
        const target = dialog === this.explore ? this.lastFocus : this.searchFocus;
        const fallback = this.querySelector(dialog === this.search ? '.fh-header__search' : matchMedia('(min-width: 900px)').matches ? '.fh-explore-trigger' : '.fh-mobile-menu');
        (target?.isConnected && target.getClientRects().length ? target : fallback)?.focus({ preventScroll: true });
      }
    }
    closeAll(restore = true) { this.searchReturn = false; this.closeDialog(this.search, restore); this.closeDialog(this.explore, restore); }
    cancelDismiss(dialog) {
      const timer = this.dismissTimers?.get(dialog);
      if (timer !== undefined) { clearTimeout(timer); this.dismissTimers.delete(dialog); }
      dialog?.classList?.remove('fh-is-closing');
    }
    animateDismiss(dialog) {
      if (!dialog?.open || this.dismissTimers?.has(dialog)) return;
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) { this.closeDialog(dialog); return; }
      this.dismissTimers ||= new Map();
      dialog.classList.add('fh-is-closing');
      const timer = setTimeout(() => {
        // A reopened surface may have replaced this timer before its callback was delivered.
        if (this.dismissTimers.get(dialog) !== timer) return;
        this.closeDialog(dialog);
      }, 160);
      this.dismissTimers.set(dialog, timer);
    }
    dismiss(dialog) {
      if (dialog === this.search && this.searchReturn) { const target = this.searchFocus; this.closeDialog(dialog, false); this.openExplore(); if (target?.getClientRects().length) target.focus({ preventScroll: true }); }
      else this.animateDismiss(dialog);
    }
    showPanel(id, focus = true) {
      this.cancelDismiss(this.explore);
      const panel = this.panels.find(p => p.dataset.panel === id);
      if (!panel) return;
      const scroll = this.scroller(); this.scrollPositions.set(this.activePanel, scroll.scrollTop);
      this.activePanel = id;
      this.querySelectorAll('[data-open-explore],[data-open-customs]').forEach(trigger => {
        const selected = trigger.matches('[data-open-customs]') ? id === 'customs' : id !== 'customs';
        trigger.setAttribute('aria-expanded', String(Boolean(this.explore.open && selected)));
      });
      this.panels.forEach(p => p.hidden = p !== panel);
      this.explore.dataset.level = id;
      this.querySelector('[data-nav-title]').textContent = panel.dataset.title || 'Explore Fathead';
      const back = this.querySelector('[data-nav-back]'); back.hidden = id === 'featured';
      back.dataset.destination = panel.dataset.parent || 'featured';
      this.querySelectorAll('.fh-explore__rail [data-panel-target]').forEach(b => {
        const selected = b.dataset.panelTarget === (panel.dataset.parent || id);
        b.classList.toggle('is-selected', selected);
        if (selected) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
      });
      scroll.scrollTop = this.scrollPositions.get(id) || 0;
      if (focus) {
        const heading = panel.querySelector('h3'); heading?.setAttribute('tabindex', '-1'); heading?.focus({ preventScroll: true });
      }
    }
    onClick(event) {
      if (this.customizerOwnsSurface()) return;
      const target = event.target.closest('button,a');
      if (!target || event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
      if (target.matches('[data-open-customs]')) {
        event.preventDefault();
        if (this.explore.open && this.activePanel === 'customs' && !this.dismissTimers?.has(this.explore)) this.dismiss(this.explore);
        else { if (!this.explore.open) this.openExplore(); this.showPanel('customs'); }
      }
      else if (target.matches('[data-open-explore]')) {
        event.preventDefault();
        if (this.explore.open && this.activePanel === 'customs') this.showPanel('featured');
        else this.explore.open && !this.dismissTimers?.has(this.explore) ? this.dismiss(this.explore) : this.openExplore();
      }
      else if (target.matches('[data-open-search]')) { event.preventDefault(); this.openSearch(); }
      else if (target.matches('[data-close]')) this.dismiss(target.closest('dialog'));
      else if (target.matches('[data-panel-target]')) this.showPanel(target.dataset.panelTarget);
      else if (target.matches('[data-nav-back]')) this.showPanel(target.dataset.destination);
      else if (target.matches('[data-browse-collections]')) { this.closeDialog(this.search, false); this.openExplore(); this.showPanel('sports'); }
      else if (target.matches('[data-clear-query]')) { this.query.value = ''; this.onQuery(); this.query.focus(); }
      else if (target.matches('[data-search-term]')) { this.query.value = target.dataset.searchTerm; this.onQuery(); this.query.focus(); }
      else if (target.matches('[data-clear-history]')) { this.history = []; try { localStorage.removeItem('fh-discovery-search-history-v2'); } catch {} this.renderHistory(); }
      else if (target.closest('[data-search-results]')) this.saveHistory(this.query.value);
    }
    filterDirectory(input) {
      const panel = input.closest('[data-panel]'), query = normalize(input.value);
      let count = 0;
      panel.querySelectorAll('.fh-directory-link').forEach(a => { a.hidden = !normalize(a.textContent).includes(query); if (!a.hidden) count++; });
      panel.querySelector('.fh-directory-empty').hidden = count > 0;
    }
    renderIdeas() {
      const node = this.querySelector('[data-search-ideas]'); node.replaceChildren();
      this.phrases.forEach(term => { const b = el('button', '', term); b.type = 'button'; b.dataset.searchTerm = term; node.append(b); });
    }
    readHistory() { try { const value = JSON.parse(localStorage.getItem('fh-discovery-search-history-v2') || '[]'); return Array.isArray(value) ? value.filter(x => typeof x === 'string' && x.length <= 100).slice(0, 6) : []; } catch { return []; } }
    saveHistory(value) { const term = value.trim().slice(0, 100); if (!term) return; this.history = [term, ...this.history.filter(x => x.toLowerCase() !== term.toLowerCase())].slice(0, 6); try { localStorage.setItem('fh-discovery-search-history-v2', JSON.stringify(this.history)); } catch {} }
    renderHistory() { this.querySelector('[data-search-history]').hidden = !this.history.length; const node = this.querySelector('[data-history-items]'); node.replaceChildren(); this.history.forEach(term => { const b = el('button', '', term); b.type = 'button'; b.dataset.searchTerm = term; node.append(b); }); }
    startPlaceholder() {
      const node = this.querySelector('[data-search-phrase]');
      if (!node || !this.phrases.length || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      let index = 0;
      const type = () => {
        if (!this.isConnected) return;
        if (document.hidden || this.explore.open || this.search.open) { this.phraseTimer = setTimeout(type, 2400); return; }
        const term = this.phrases[index++ % this.phrases.length]; let character = 0;
        const tick = () => { if (!this.isConnected) return; node.textContent = term.slice(0, ++character); this.phraseTimer = setTimeout(character < term.length ? tick : type, character < term.length ? 65 : 3800); };
        tick();
      };
      this.phraseTimer = setTimeout(type, 3000);
    }
    initCustomsVideo() {
      this.customsVideo = this.querySelector('[data-customs-video]');
      if (!this.customsVideo) return;
      this.customsMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.customsVisible = false;
      this.listen(this.customsVideo, 'playing', () => { if (!this.shouldPlayCustomsVideo()) { this.customsVideo.pause(); return; } this.customsVideo.setAttribute('data-playing', ''); });
      this.listen(this.customsVideo, 'error', () => this.customsVideo.removeAttribute('data-playing'));
      this.listen(document, 'visibilitychange', () => this.syncCustomsVideo());
      this.listen(this.customsMotion, 'change', () => this.syncCustomsVideo());
      this.listen(navigator.connection, 'change', () => this.syncCustomsVideo());
      this.listen(this.search, 'close', () => this.syncCustomsVideo());
      this.customsObserver = new IntersectionObserver(entries => { this.customsVisible = entries[0]?.isIntersecting === true; this.syncCustomsVideo(); }, { root: this.querySelector('.fh-search__body'), threshold: .25 });
      this.customsObserver.observe(this.customsVideo);
    }
    shouldPlayCustomsVideo() {
      const constrained = this.customsMotion?.matches || navigator.connection?.saveData || ['slow-2g', '2g'].includes(navigator.connection?.effectiveType);
      return this.search?.open && !this.discover?.hidden && !document.hidden && this.customsVisible && !constrained;
    }
    syncCustomsVideo() {
      const video = this.customsVideo;
      if (!video) return;
      if (!this.shouldPlayCustomsVideo()) { video.pause(); return; }
      // No src/source/autoplay attribute in initial HTML: no video fetch before visible discovery.
      if (!video.getAttribute('src')) { video.muted = true; video.src = video.dataset.src; }
      if (video.paused) video.play().catch(() => { /* Keep the still image if autoplay is blocked. */ });
    }
    matchingCollections(query) {
      const tokens = normalize(query).split(' ').filter(Boolean);
      if (!tokens.length) return [];
      return this.index.filter(item => tokens.every(token => normalize(item.title + ' ' + item.group).includes(token))).sort((a, b) => (normalize(a.title) === normalize(query) ? -1 : 0) - (normalize(b.title) === normalize(query) ? -1 : 0)).slice(0, 8);
    }
    searchURL(query) { const url = new URL(this.dataset.searchUrl || '/search', location.origin); url.searchParams.set('q', query); url.searchParams.set('type', 'product'); url.searchParams.set('options[unavailable_products]', 'hide'); return url.pathname + url.search; }
    onQuery() {
      window.FHSearchRecovery?.cancel(this.results);
      clearTimeout(this.analyticsTimer); clearTimeout(this.debounce); this.request?.abort(); const revision = ++this.revision;
      const query = this.query.value.trim().slice(0, 150);
      this.querySelector('[data-query-value]').value = query;
      this.querySelector('[data-clear-query]').hidden = !query;
      this.discover.hidden = !!query; this.results.hidden = !query; this.syncCustomsVideo();
      if (!query) { this.results.replaceChildren(); this.results.removeAttribute('aria-busy'); this.status.textContent = ''; this.renderHistory(); return; }
      const collections = this.matchingCollections(query);
      this.renderResults(query, collections, [], { loading: true });
      this.debounce = setTimeout(() => this.fetchResults(query, revision, collections), 180);
    }
    async fetchJSON(url, signal, timeout = 2600) {
      // Bound the complete decoded response, including a stalled body after headers.
      const maxBytes = 256 * 1024;
      const started = performance.now();
      const controller = new AbortController();
      const abort = () => controller.abort();
      const aborted = () => Object.assign(new Error('Search request aborted'), { name: 'AbortError' });
      const cancelBody = body => { try { body?.cancel?.().catch(() => {}); } catch {} };
      let rejectStopped;
      const stopped = new Promise((_, reject) => { rejectStopped = reject; });
      const stop = () => rejectStopped(aborted());
      controller.signal.addEventListener('abort', stop, { once: true });
      signal.addEventListener('abort', abort, { once: true });
      const timer = setTimeout(abort, timeout);
      let response, reader, complete = false;
      const checkDeadline = () => {
        if (controller.signal.aborted || signal.aborted || performance.now() - started >= timeout) { abort(); throw aborted(); }
      };
      try {
        if (signal.aborted) throw aborted();
        const request = fetch(url, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(result => {
          if (controller.signal.aborted) { cancelBody(result.body); throw aborted(); }
          return result;
        });
        response = await Promise.race([request, stopped]);
        checkDeadline();
        if (!response.ok) { const error = new Error(`Search HTTP ${response.status}`); error.status = response.status; throw error; }
        const announced = response.headers.get('content-length');
        if (announced && /^\d+$/.test(announced) && Number(announced) > maxBytes) throw new Error('Search response too large');
        if (!response.body?.getReader) throw new Error('Search response body unavailable');
        reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8', { fatal: true });
        let bytes = 0, chunks = 0, emptyChunks = 0, text = '';
        while (true) {
          checkDeadline();
          if (++chunks % 64 === 0) await Promise.race([new Promise(resolve => setTimeout(resolve, 0)), stopped]);
          const { value, done } = await Promise.race([reader.read(), stopped]);
          checkDeadline();
          if (done) { complete = true; break; }
          if (!value.byteLength) { if (++emptyChunks > 32) throw new Error('Invalid empty search stream'); continue; }
          emptyChunks = 0;
          bytes += value.byteLength;
          if (bytes > maxBytes) throw new Error('Search response too large');
          text += decoder.decode(value, { stream: true });
        }
        text += decoder.decode();
        checkDeadline();
        return JSON.parse(text);
      } finally {
        clearTimeout(timer);
        signal.removeEventListener('abort', abort);
        controller.signal.removeEventListener('abort', stop);
        if (!complete) {
          controller.abort();
          cancelBody(reader || response?.body);
        }
        if (reader) { try { reader.releaseLock(); } catch {} }
      }
    }
    async fetchResults(query, revision, collections) {
      const started = performance.now();
      const nativeMode = this.dataset.searchMode === 'native';
      const canonicalMode = this.dataset.searchContract === 'fh-search/2';
      let canonical = null;
      const controller = new AbortController(); this.request = controller;
      let products = [], provider = 'fh-search', extra = [], failed = false, recovery=null, strategy='baseline', alternates=null, emptyReason=null, related=null;
      try {
        if (nativeMode || this.providerDisabled || Date.now() < (this.providerRetryAt || 0)) throw new Error('FH provider circuit open');
        const endpoint = internalURL(this.dataset.searchEndpoint || '/apps/search');
        if (!endpoint) throw new Error('Invalid search endpoint');
        const canonicalParams = canonicalMode ? `&contract=fh-search/2&language=${encodeURIComponent(this.dataset.language||'')}` : '';
        const data = await this.fetchJSON(`${endpoint.replace(/\/$/, '')}/suggest?q=${encodeURIComponent(query)}&limit=6&telemetry=none&country=${encodeURIComponent(this.dataset.country||'US')}&currency=${encodeURIComponent(this.dataset.currency||'USD')}${canonicalParams}`, controller.signal, 1500);
        if (data.authoritative === true) {
          alternates = data.alternates || null;
          emptyReason = data.emptyReason || null;
          related = data.related && Array.isArray(data.related.products) ? data.related : null;
        }
        if (canonicalMode) {
          if (!window.FHCanonicalCards) throw new Error('Canonical card consumer unavailable');
          canonical = window.FHCanonicalCards.parseResponse(data, {country:this.dataset.country,language:this.dataset.language,currency:this.dataset.currency,root:this.dataset.rootUrl,query,limit:6});
          products = canonical.cards;
          // Stage the entire validated group before replacing visible results.
          canonical.nodes = products.map((product,index) => window.FHCanonicalCards.renderCard(product,{country:this.dataset.country,language:this.dataset.language,currency:this.dataset.currency,root:this.dataset.rootUrl,position:index+1}));
        } else {
        if (!Array.isArray(data.products)) throw new Error('Invalid FH Search response');
        products = data.products.filter(product => product.available === true);
        // Refill through Shopify when the app response contains unavailable or unverified products.
        if (products.length !== data.products.length) throw new Error('FH availability requires Shopify verification');
        if (data.provider === 'shopify-fallback') {
          provider = 'shopify-fallback';
          products = retainPredictiveIntent(query, products);
        }
        recovery=data.recovery||null;strategy=data.strategy==='learned'?'learned':'baseline';
        extra = Array.isArray(data.collections) ? data.collections : [];
        if (provider === 'shopify-fallback') extra = retainPredictiveIntent(query, extra, false);
        // A cold or empty index must not turn valid Shopify searches into empty successes.
        if (!products.length && data.authoritative !== true) throw new Error('FH Search empty; verify against Shopify');
        }
      } catch (error) {
        if (controller.signal.aborted || revision !== this.revision) return;
        alternates = null;
        emptyReason = null;
        if (error.status === 401 || error.status === 403) this.providerDisabled = true;
        if (!nativeMode) this.providerRetryAt = Date.now() + 30000;
        provider = 'shopify-fallback'; canonical = null;
        if (nativeMode) delete this.dataset.searchProviderIssue;
        else this.dataset.searchProviderIssue = error.status ? `http-${error.status}` : 'unavailable-or-empty';
        try {
          const root = this.dataset.rootUrl || '/';
          const params = new URLSearchParams({ q: query, 'resources[type]': 'product,collection', 'resources[limit]': '6', 'resources[limit_scope]': 'each', 'resources[options][unavailable_products]': 'hide' });
          const data = await this.fetchJSON(`${root}search/suggest.json?${params}`, controller.signal, Math.max(300, 2800 - (performance.now()-started)));
          products = retainPredictiveIntent(query, (data.resources?.results?.products || []).filter(product => product.available !== false));
          extra = retainPredictiveIntent(query, data.resources?.results?.collections || [], false);
          if (!data.resources?.results) throw new Error('Invalid predictive response');
        } catch { if (controller.signal.aborted || revision !== this.revision) return; failed = true; }
      }
      if (controller.signal.aborted || revision !== this.revision || !this.search.open) return;
      this.dataset.searchProvider = provider;
      // Native suggestions attach Shopify tracking parameters. Identity is the
      // destination path, so the same curated collection remains a single chip.
      const collectionPath = value => { const url = internalURL(value); return url ? new URL(url, location.origin).pathname.replace(/\/+$/, '') : null; };
      const seen = new Set(collections.map(item => collectionPath(item.url)).filter(Boolean));
      extra.forEach(item => { const url = internalURL(item.url || `/collections/${encodeURIComponent(item.handle || '')}`), path = collectionPath(url); if (url && path && !seen.has(path)) { seen.add(path); collections.push({ ...item, url }); } });
      this.renderResults(query, collections.slice(0, 8), products.slice(0, 6), { failed, provider, canonicalCards:canonical?.nodes, alternates, emptyReason, related });
      if(this.results)this.results.dataset.strategy=strategy;
      const responseMs = Math.min(120000, Math.round(performance.now()-started));
      clearTimeout(this.analyticsTimer);
      const details = {query, strategy, resultsCount: products.length, collectionCount: collections.slice(0,8).length, provider, latencyMs: responseMs, outcome: failed ? 'error' : products.length ? 'results' : 'zero_search'};
      const impressionId = window.FHDiscoveryAnalytics?.results(details);
      window.FHDiscoveryAnalytics?.observeProducts?.(this.results,{query,surface:'suggest',strategy,impressionId});
      if(!failed&&!products.length&&!alternates&&emptyReason!=='availability_only'&&emptyReason!=='shopper_filters')window.FHSearchRecovery?.show(this.results,query,{rule:recovery,country:this.dataset.country||'US',impressionId});
      if (failed) window.FHDiscoveryAnalytics?.track('provider_error', {...details, surface:'suggest', outcome:'error', latencyMs:null, impressionId});
      this.analyticsTimer = setTimeout(() => { if(revision===this.revision&&this.search.open)window.FHDiscoveryAnalytics?.track('search_settled',{...details,surface:'suggest',impressionId,latencyMs:null}); },700);
      this.dispatchEvent(new CustomEvent('fh:search:results', { bubbles: true, detail: { provider, results: products.length, collections: collections.length, failed } }));
    }
    // The query keeps its quotes and every character; only the break points change.
    // A long unbroken query may wrap mid-word, so the closing quote travels with the
    // tail of the query instead of stranding itself on a line of its own.
    resultsHeading(query) {
      const heading = el('h2', 'fh-results-heading');
      const quoted = el('span', 'fh-results-heading__query');
      const characters = Array.from(String(query));
      const tail = characters.splice(Math.max(0, characters.length - 2));
      quoted.append(`“${characters.join('')}`, el('span', 'fh-results-heading__tail', `${tail.join('')}”`));
      heading.append('Results for ', quoted);
      return heading;
    }
    alternateCollections(collections, heading) {
      const section = el('div', 'fh-search-next');
      section.dataset.alternateSection = '';
      section.append(el('h3', 'fh-section-heading', heading || 'Collections to explore'));
      const grid = el('div', 'fh-search-collections');
      collections.forEach(item => {
        const href = internalURL(item.url);
        if (!href) return;
        const card = el('a', 'fh-discovery-card');
        card.href = href;
        const src = imageURL(item.image);
        const media = el('div', src ? 'fh-discovery-card__image' : 'fh-discovery-card__image fh-discovery-card__image--fallback');
        if (src) {
          const img = el('img');
          const sized = new URL(src);
          sized.searchParams.set('width', '800');
          img.src = sized.href;
          img.alt = '';
          img.width = 800;
          img.height = 450;
          img.loading = 'lazy';
          media.append(img);
        } else media.append(el('span', 'fh-discovery-card__monogram', String(item.title || '').slice(0, 1)));
        const label = el('div', 'fh-discovery-card__label');
        label.append(el('span', '', item.title), arrowIcon());
        card.append(media, label);
        grid.append(card);
      });
      section.append(grid);
      return section;
    }
    alternateProducts(products) {
      const section = el('div', 'fh-search-next');
      section.append(el('h3', 'fh-section-heading', 'From those collections'));
      const group = el('div', 'fh-product-results');
      products.slice(0, 6).forEach((product, index) => {
        const url = internalURL(product.url || `/products/${encodeURIComponent(product.handle || '')}`);
        if (!url) return;
        const a = el('a', 'fh-search-product');
        a.href = url;
        a.dataset.productId = String(product.id || product.handle || '');
        a.dataset.position = String(index + 1);
        const media = el('div', 'fh-search-product__image');
        const src = imageURL(product.image);
        if (src) {
          const img = el('img');
          img.src = src;
          img.alt = '';
          img.width = 640;
          img.height = 640;
          img.loading = 'lazy';
          media.append(img);
        }
        a.append(media, el('h3', '', product.title));
        const price = Number(product.price);
        if (Number.isFinite(price)) {
          try {
            a.append(el('p', '', new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: product.currency || this.dataset.currency || 'USD' }).format(price)));
          } catch {}
        }
        window.FHProductCards?.enhance(a, product);
        group.append(a);
      });
      section.append(group);
      return section;
    }
    nextSteps() {
      const root = (this.dataset.rootUrl || '/').replace(/\/$/, '');
      const section = el('div', 'fh-search-next');
      section.append(el('h3', 'fh-section-heading', 'More to explore'));
      const grid = el('div', 'fh-search-next__grid');
      const customs = this.querySelector('.fh-customs-link');
      const links = [
        [root + '/collections/best-selling-products?sort_by=best-selling', 'Best sellers'],
        [root + '/collections/custom-big-head-cutouts', 'Big Heads'],
        [customs ? customs.href : root + '/pages/customs', 'Create a custom Fathead'],
      ];
      for (const [href, label] of links) {
        const card = el('a', 'fh-search-next__card', label);
        card.href = href;
        grid.append(card);
      }
      section.append(grid);
      return section;
    }
    relatedGroup(group) {
      const section = el('div', 'fh-search-related');
      section.append(el('h3', 'fh-section-heading', group.title || 'Princess'));
      const href = internalURL(group.url);
      if (href) {
        const card = el('a', 'fh-discovery-card');
        card.href = href;
        const src = imageURL(group.image);
        const media = el('div', src ? 'fh-discovery-card__image' : 'fh-discovery-card__image fh-discovery-card__image--fallback');
        if (src) {
          const img = el('img');
          const sized = new URL(src);
          sized.searchParams.set('width', '800');
          img.src = sized.href;
          img.alt = '';
          img.width = 800;
          img.height = 450;
          img.loading = 'lazy';
          media.append(img);
        } else media.append(el('span', 'fh-discovery-card__monogram', String(group.title || '').slice(0, 1)));
        const label = el('div', 'fh-discovery-card__label');
        label.append(el('span', '', group.title || 'Princess'), arrowIcon());
        card.append(media, label);
        const grid = el('div', 'fh-search-collections');
        grid.append(card);
        section.append(grid);
      }
      const list = el('div', 'fh-product-results');
      (group.products || []).slice(0, 6).forEach((product, index) => {
        const url = internalURL(product.url || `/products/${encodeURIComponent(product.handle || '')}`);
        if (!url) return;
        const a = el('a', 'fh-search-product');
        a.href = url;
        a.dataset.productId = String(product.id || product.handle || '');
        a.dataset.position = String(index + 1);
        const media = el('div', 'fh-search-product__image');
        const src = imageURL(product.image);
        if (src) {
          const img = el('img');
          const sized = new URL(src);
          sized.searchParams.set('width', '320');
          img.src = sized.href;
          img.alt = '';
          img.width = 320;
          img.height = 320;
          img.loading = 'lazy';
          media.append(img);
        }
        a.append(media, el('h3', '', product.title));
        list.append(a);
      });
      if (list.childNodes.length) section.append(list);
      return section;
    }
    renderResults(query, collections, products, options = {}) {
      this.results.replaceChildren();
      this.results.classList.remove('fh-search-results--guide');
      this.results.setAttribute('aria-busy', options.loading ? 'true' : 'false');
      this.results.append(this.resultsHeading(query));
      const soldOutCards = !options.loading && !options.failed && !products.length && options.emptyReason === 'availability_only' && collections.length;
      if (collections.length && !soldOutCards) {
        this.results.append(el('h3', 'fh-section-heading', 'Collections'));
        const group = el('div', 'fh-collection-results');
        collections.forEach(item => { const url = internalURL(item.url); if (!url) return; const a = el('a', '', item.title); a.href = url; group.append(a); });
        this.results.append(group);
      }
      if (options.loading) {
        const skeleton = el('div', 'fh-search-loading'); skeleton.setAttribute('aria-hidden', 'true'); for (let i = 0; i < 3; i++) skeleton.append(el('div')); this.results.append(skeleton); this.status.textContent = 'Searching Fathead'; return;
      }
      if (products.length) {
        this.results.append(el('h3', 'fh-section-heading', 'Products'));
        const group = el('div', 'fh-product-results');
        products.forEach((product, index) => {
          if (options.canonicalCards) {
            group.append(options.canonicalCards[index]);
            return;
          }
          const url = internalURL(product.url || `/products/${encodeURIComponent(product.handle || '')}`); if (!url) return;
          const a = el('a', 'fh-search-product'); a.href = url; a.dataset.productId = String(product.id || product.handle || url.split('/products/')[1]); a.dataset.position = String(index + 1);
          const media = el('div', 'fh-search-product__image');
          const src = imageURL(product.image || product.featured_image?.url);
          if (src) { const img = el('img'); const sized = w => { const url = new URL(src); url.searchParams.set('width', String(w)); return url.href; }; img.src = sized(640); img.srcset = [160, 240, 320, 480, 640].map(w => `${sized(w)} ${w}w`).join(', '); img.sizes = '(max-width:599px) 92px, 104px'; img.alt = ''; img.width = 640; img.height = 640; img.loading = 'lazy'; media.append(img); }
          a.append(media, el('h3', '', product.title));
          const price = Number(product.price); if (Number.isFinite(price)) { try { a.append(el('p', '', `${product.price_max > price ? 'From ' : ''}${new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: product.currency || this.dataset.currency || 'USD' }).format(price)}`)); } catch {} }
          window.FHProductCards?.enhance(a, product);
          group.append(a);
        });
        this.results.append(group);
        if (options.related && Array.isArray(options.related.products) && options.related.products.length) this.results.append(this.relatedGroup(options.related));
        const all = el('a', 'fh-search-all', 'View all results →'); all.href = this.searchURL(query); this.results.append(all);
      } else if (options.failed) {
        const empty = el('div', 'fh-search-empty');
        empty.append(el('h3', '', 'Keep exploring.'));
        empty.append(el('p', '', 'Quick results are taking a moment. Open the full results below, or explore a collection.'));
        this.results.append(empty);
        const all = el('a', 'fh-search-all', 'View all results →'); all.href = this.searchURL(query); this.results.append(all);
      } else {
        const picks = options.emptyReason === 'availability_only' || options.emptyReason === 'shopper_filters' ? null : options.alternates;
        this.results.classList.add('fh-search-results--guide');
        const empty = el('div', 'fh-search-empty');
        const headline = options.emptyReason === 'availability_only' ? 'We carry this.' : options.emptyReason === 'shopper_filters' ? 'Try a little more room.' : picks?.headline || 'We don’t currently have products for that search, but we thought you might like these.';
        const detail = options.emptyReason === 'availability_only' ? 'There aren’t any in-stock products to show right now.' : options.emptyReason === 'shopper_filters' ? 'Remove a filter to see more products.' : (picks?.detail || '');
        empty.classList.add('fh-search-note');
        empty.append(el('h3', '', headline));
        if (detail) empty.append(el('p', '', detail));
        this.results.append(empty);
        if (picks && picks.intent) this.results.dataset.alternateIntent = String(picks.intent);
        if (picks && Array.isArray(picks.collections) && picks.collections.length) {
          const block = this.alternateCollections(picks.collections, picks.sectionLabel);
          block.querySelectorAll('a').forEach((card, index) => {
            card.dataset.recoveryRule = String(picks.intent);
            card.dataset.recoveryQuery = query;
            card.dataset.recoveryPosition = String(index + 1);
            card.dataset.alternateIntent = String(picks.intent);
          });
          this.results.append(block);
          if (picks.relationship !== 'no_honest_substitute' && picks.relationship !== 'non_shopping' && Array.isArray(picks.products) && picks.products.length) {
            this.results.append(this.alternateProducts(picks.products));
          }
        } else if (soldOutCards) {
          this.results.append(this.alternateCollections(collections, 'Collections'));
        } else if (options.emptyReason !== 'availability_only' && options.emptyReason !== 'shopper_filters' && (!picks || picks.relationship !== 'non_shopping')) {
          this.results.append(this.nextSteps());
        }
        const more = el('div', 'fh-guide-more');
        const all = el('a', 'fh-guide-button', 'View all results');
        all.href = this.searchURL(query);
        more.append(all);
        this.results.append(more);
      }
      this.status.textContent = `${collections.length} collections and ${products.length} products shown${options.failed ? '. Quick results unavailable; full search is available.' : '.'}`;
    }
  }
  customElements.define('fh-discovery', FHDiscovery);
})();
